import "./SupportCenterScreenComponent.scss";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent,
} from "../../shared/components/tabs/tabsComponent";
import FeedbackComponent from "../../shared/components/feedback/feedbackComponent";
import {useSearchParams} from "react-router-dom";
import {setCurrentNavParams, setSideMenuView} from "../../store/actions/header.action";
import {useDispatch} from "react-redux";
import useFetch from "../../shared/hooks/useFetch";
import {APIConfig, ImageConfig} from "../../constants";
import LottieFileGenarationComponent
    from "../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {CommonService} from "../../shared/services";

// Constants
const MANUALS_TAB = 'manuals';
const FAQS_TAB = 'faqs';

interface AttachmentDetails {
    id: number;
    helpSectionId: number;
    title: string;
    description: string;
    secureUrl: string;
    mimeType: string;
    size: string;
    attachmentType: string;
    added_by: number;
    created_at: string;
    updated_at: string;
    url: string;
}

interface SectionDetails {
    id: number;
    sequence: number;
    title: string;
    description: string;
    added_by: number;
    created_at: string;
    updated_at: string;
    attachments: AttachmentDetails[];
}

const HelpSectionList = ({
                             sections,
                             selectedSection,
                             onSelectSection,
                         }: {
    sections: SectionDetails[];
    selectedSection: SectionDetails | null;
    onSelectSection: (section: SectionDetails) => void;
}) => (
    <ol className="help-section-list-wrapper">
        {sections.map((section) => (
            <li
                key={section.id}
                onClick={() => onSelectSection(section)}
                className={`help-section-list-item ${section.id === selectedSection?.id ? 'active' : ''}`}
            >
                {section.sequence}{". "} {section.title}
            </li>
        ))}
    </ol>
);

const HelpSectionDetails = (props: { section: SectionDetails | null; }) => {
    const {section} = props;
    if (!section) return null;

    const {title, description, attachments, sequence} = section;
    const renderAttachments = (attachments: AttachmentDetails[]) => {
        if (attachments.length === 0) {
            return <FeedbackComponent color={'error'} type={'error'} className="mrg-bottom-30">
                No article available for this section.
            </FeedbackComponent>
        }

        return (
            <div className="display-cards-grid-view-wrapper">
                {attachments.map(({id, title, description, url, mimeType}) => (
                    <div
                        onClick={() => {
                            const attachment = {
                                name: title,
                                type: mimeType,
                                key: id.toString(),
                                url: url,
                            }
                            CommonService._communications.AttachmentPreviewSubject.next([attachment])
                        }}
                        className="help-article-card" key={id}>
                        <div className="article-card-thumbnail">
                            <img
                                src={ImageConfig.pdfImage}
                                alt={title}
                                width={100}
                            />
                        </div>
                        <div className="article-card-content">
                            <div className="article-card-title">{title}</div>
                            <div className="article-card-description">{description}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="section-details">
            <FeedbackComponent className="mrg-bottom-30 article-info">
                <div className="article-header">{`${sequence}. ${title}`}</div>
                <div className="article-description">{description}</div>
            </FeedbackComponent>
            {renderAttachments(attachments)}
        </div>
    );
};

const SupportCenterScreenComponent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentTab, setCurrentTab] = useState<string>(MANUALS_TAB);
    const [selectedSection, setSelectedSection] = useState<SectionDetails | null>(null);
    const dispatch = useDispatch();

    const {
        response: sectionData = [],
        loading: isSectionDataLoading,
        fetchData: fetchSection
    } = useFetch<SectionDetails[]>()
    const helpSectionList = useMemo(() => sectionData || [], [sectionData])

    const fetchHelpSectionData = useCallback(() => {
        fetchSection({
            url: APIConfig.GET_SUPPORT_CENTER_LIST.URL,
            method: APIConfig.GET_SUPPORT_CENTER_LIST.METHOD,
            payload: {}
        });
    }, [fetchSection]);

    useEffect(() => {
        fetchHelpSectionData()
    }, [fetchHelpSectionData]);

    useEffect(() => {
        const activeSectionId = searchParams.get('sectionId');
        const defaultSelectedSection = helpSectionList.length > 0 ? helpSectionList[0] : null;

        if (activeSectionId) {
            const section = helpSectionList.find((section) => +(section.id) === parseInt(activeSectionId));
            setSelectedSection(section || defaultSelectedSection);
        } else {
            setSelectedSection(defaultSelectedSection);
        }
    }, [helpSectionList, searchParams]);

    useEffect(() => {
        dispatch(setCurrentNavParams("Support Center", ""));
        dispatch(setSideMenuView('default'));
    }, [dispatch]);

    const handleSelectSection = useCallback(
        (section: SectionDetails) => {
            setSelectedSection(section);
            searchParams.set("sectionId", section.id.toString());
            setSearchParams(searchParams);
        },
        [searchParams, setSearchParams]
    );

    return (
        <div className="help-screen-component screen">
            <div className="screen-content-wrapper">
                <div className="page-data-wrapper">
                    <TabsWrapperComponent className="tab-content-wrapper">
                        <TabsComponent
                            value={currentTab}
                            allowScrollButtonsMobile={false}
                            variant="fullWidth"
                            onUpdate={(e: any, v: any) => setCurrentTab(v)}
                        >
                            <TabComponent label="MANUALS" value={MANUALS_TAB}/>
                            <TabComponent label="FAQS" value={FAQS_TAB} disabled={true}/>
                        </TabsComponent>

                        <TabContentComponent selectedTab={currentTab} value={MANUALS_TAB}>
                            {
                                isSectionDataLoading ? (
                                    <div className="h-v-center">
                                        <LottieFileGenarationComponent
                                            loop={true}
                                            autoplay={true}
                                            animationData={ImageConfig.LoadingLottieFile}/>
                                    </div>
                                ) : (
                                    <div className="manual-tab-wrapper">
                                        <div className="help-section-navigation">
                                            <div className="help-section-title">Sections List:</div>
                                            <HelpSectionList
                                                sections={helpSectionList}
                                                selectedSection={selectedSection}
                                                onSelectSection={handleSelectSection}
                                            />
                                        </div>
                                        <div className="help-section-article-dashboard">
                                            <HelpSectionDetails section={selectedSection}/>
                                        </div>
                                    </div>
                                )
                            }
                        </TabContentComponent>

                        <TabContentComponent selectedTab={currentTab} value={FAQS_TAB}>
                            FAQ Coming Soon
                        </TabContentComponent>
                    </TabsWrapperComponent>
                </div>
            </div>
        </div>
    );
};

export default SupportCenterScreenComponent;
